import { useMutation } from "@tanstack/react-query";

import { ApiError } from "@config/apiClient";
import { queryClient } from "@config/queryClient";
import ToastService from "@services/toast.service";
import { ERROR_STRINGS, ERROR_MESSAGES } from "@config/messages";
import relationService from "@services/relation.service";
import { useBugsnagErrorHandler } from "@hooks/useBugsnagErrorHandler";
import { DisconnectedError } from "@app-types/errors";
import { IRelationResponse, IRelationToCreate } from "@interfaces/relation.interface";
import {
  CREATE_OBJECT_TAG_MUTATION_KEY,
  REMOVE_OBJECT_TAG_MUTATION_KEY,
  SUGGESTED_TAGGING_OPTIONS_QUERY_KEY,
} from "@config/query";

/**
 * Hook to add a relation
 * @param objectId - thread ID for emails or meeting ID for meetings
 * @param onSuccess - Optional callback executed on successful addition
 */
export function useAddRelation(objectId: string, onSuccess?: (data: IRelationResponse) => void) {
  const { notifyBugsnag } = useBugsnagErrorHandler();

  return useMutation<IRelationResponse, Error, IRelationToCreate>({
    mutationKey: [CREATE_OBJECT_TAG_MUTATION_KEY, objectId],
    mutationFn: async ({ ...body }) => {
      return await relationService.addTag(body);
    },
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      if (error instanceof DisconnectedError) return;
      notifyBugsnag(error, ERROR_STRINGS.ADD_TAG);
      ToastService.showError(error as ApiError, ERROR_MESSAGES.ADD_TAG);
    },
  });
}

/**
 * Hook to delete a relation
 * @param objectId - thread ID for emails or meeting ID for meetings
 * @param onSuccess - Optional callback executed on successful deletion
 */
export function useDeleteRelation(objectId: string, onSuccess?: () => void) {
  const { notifyBugsnag } = useBugsnagErrorHandler();

  return useMutation({
    mutationKey: [REMOVE_OBJECT_TAG_MUTATION_KEY, objectId],
    mutationFn: async (id: string) => {
      return await relationService.deleteTag(id);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [SUGGESTED_TAGGING_OPTIONS_QUERY_KEY, objectId],
      });
      onSuccess?.();
    },
    onError: (error) => {
      if (error instanceof DisconnectedError) return;
      notifyBugsnag(error, ERROR_STRINGS.REMOVE_TAG);
      ToastService.showError(error as ApiError, ERROR_MESSAGES.REMOVE_TAG);
    },
  });
}
